import type { IDictionary } from './appstate';
import type {
	ApiError,
	ApiException,
	ApiFailure,
	ApiResult,
	ApiSuccess,
} from './async-actions';
import { endpoint } from './helpers/Endpoints';
import { expiresInDefaultValue, key } from './helpers/LocalStorage';

export interface IFetchConfig {
	uri: string;
	headers?: Headers;
	cache?: RequestCache;
	responseKind?: 'json' | 'blob';
}

export interface PostConfig extends IFetchConfig {
	method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
	body?: string | FormData;
}

export interface GetConfig extends IFetchConfig {
	method: 'GET';
}

export type RequestConfig = PostConfig | GetConfig;

function getRequestOptions(config: RequestConfig): [string, RequestInit] {
	const headers = getHeaders(config);

	let body: BodyInit | undefined;

	if (config.method === 'POST' || config.method === 'PUT') {
		body = config.body;
	}

	const options: RequestInit = {
		method: config.method || 'GET',
		headers,
		cache: config.cache || 'no-store',
		credentials: 'include',
		body,
	};

	const uri = config.uri;

	return [uri, options];
}

function getHeaders(config: RequestConfig): Headers {
	if (config.headers) {
		return config.headers;
	}

	const headers = new Headers();
	headers.append('Accept', 'application/json');

	if (
		config.method === 'POST' ||
		config.method === 'PUT' ||
		config.method === 'PATCH' ||
		config.method === 'DELETE'
	) {
		const isJsonBody = !(config.body instanceof FormData);

		if (isJsonBody) {
			headers.append('Content-Type', 'application/json');
		}
	}

	return headers;
}

/** This mirrors APIResponse.cs */
export interface ApiResponse<T> {
	errors: string[];
	metadata: IDictionary<any>;
	statusCode: number;
	value?: T;
}

/**
 * @deprecated Use `@dsx/hub-utils`
 */
export function fetchWithRefresh(
	input: RequestInfo,
	init?: RequestInit,
	preventTimeoutExtension = false
): Promise<Response> {
	const initialCallPromise = fetch(input, init);
	return initialCallPromise.then((response) => {
		if (!preventTimeoutExtension) {
			const localStorageExpiresIn = parseInt(
				window.localStorage.getItem(key.expiresIn),
				10
			);

			const expiresIn = isNaN(localStorageExpiresIn)
				? expiresInDefaultValue
				: localStorageExpiresIn;

			window.localStorage.setItem(
				key.expiresAt,
				'' + (new Date().getTime() + expiresIn)
			);
		}

		if (response.status !== 401) {
			return initialCallPromise;
		} else {
			const refreshInit: RequestInit = {
				method: 'POST',
				cache: 'no-store',
				credentials: 'include',
			};

			const refreshCallPromise = fetch(endpoint.refreshToken(), refreshInit);

			return refreshCallPromise.then((refreshResponse) => {
				if (refreshResponse.status === 200) {
					return fetch(input, init);
				} else {
					return initialCallPromise;
				}
			});
		}
	});
}

/**
 * @deprecated Use `@dsx/hub-utils`
 */
export async function fetch2<TData>(
	config: RequestConfig,
	preventTimeoutExtension = false
): Promise<ApiResult<TData>> {
	const [uri, options] = getRequestOptions(config);

	try {
		const response = await fetchWithRefresh(
			uri,
			options,
			preventTimeoutExtension
		);

		// ==============================
		// Naming conventions used here:
		// Success (2xx): everything as expected
		// Failure (4xx): server working as intended, request had problems (for example, validation)
		// Error (5xx): server not working as intended, could not respond to request
		// Exception: problem with network
		// ==============================

		// response.ok puts us in the 200-299 range
		// HTTP OK
		if (response.ok) {
			const responseKind = config.responseKind || 'json';

			if (responseKind === 'json') {
				let data = { value: null, metadata: {} };

				try {
					data = await response.json();
				} catch (e) {
					// This is fine.
				}

				const api: ApiSuccess<TData> = {
					value: data.value,
					hasErrors: false,
					metaData: data.metadata,
					ok: true,
					namespace: 'ui-api-result',
					kind: 'success',
				};

				return api;
			}

			if (responseKind === 'blob') {
				const blob = await response.blob();
				const api: ApiSuccess<TData> = {
					ok: true,
					hasErrors: false,
					metaData: {},
					namespace: 'ui-api-result',
					kind: 'success',
					value: {} as TData,
					blob,
				};
				return api;
			}
		}

		if (response.status >= 400 && response.status < 500) {
			const data = await response.json();
			const api: ApiFailure = {
				errors: data.errors,
				hasErrors: false,
				ok: false,
				namespace: 'ui-api-result',
				kind: 'failure',
				metaData: data.metadata,
			};
			return api;
		}

		if (response.status >= 500 && response.status < 600) {
			return {
				namespace: 'ui-api-result',
				ok: false,
				kind: 'error',
				response,
			} as ApiError;
		}

		throw new Error(`Unknown response: ${response.status}`);
	} catch (error) {
		// Network or other exception
		// https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API/Using_Fetch#Checking_that_the_fetch_was_successful
		// globalActionConfig.onException(error);

		return {
			namespace: 'ui-api-result',
			ok: false,
			kind: 'exception',
			ex: error,
		} as ApiException;
	}
}

export class ApiRequestError extends Error {
	public errors: string[];
	constructor(errors: string[]) {
		super('Bad request\n' + errors.join('\n'));
		this.errors = errors;
		this.name = 'ApiRequestError';
	}
}

export class ApiServerError extends Error {
	public response: Response;
	constructor(response: Response) {
		super('Server error');
		this.response = response;
		this.name = 'ApiServerError';
	}
}

/**
 * @deprecated Use `@dsx/hub-utils`
 */
export function unwrapFetch2<T>(promise: Promise<ApiResult<T>>): Promise<T> {
	return promise.then((result) => {
		if (result.kind === 'success') {
			return result.value;
		} else if (result.kind === 'failure') {
			throw new ApiRequestError(result.errors);
		} else if (result.kind === 'error') {
			throw new ApiServerError(result.response);
		}
	});
}
